import type { ChangeEvent } from 'react'

import CheckBox from '../../CheckBox'
import InputGroup from '../../InputGroup'
import type {
  TNewUploadManualFileOptions,
  TVehicleFuel,
} from '../../../hooks/useNewUpload'

type Props = {
  fuel: TVehicleFuel
  hardcutRpmError?: string | null
  manualFileOptions: TNewUploadManualFileOptions
  setManualFileOptions: (updates: Partial<TNewUploadManualFileOptions>) => void
}

const BuildManualFileOptions = (props: Props) => {
  const { fuel, hardcutRpmError, manualFileOptions, setManualFileOptions } =
    props

  return (
    <div className="space-y-4">
      <p className="mb-2 block text-sm font-medium leading-5 text-black">
        File options
      </p>
      <div className="mt-4 grid items-start gap-2 md:grid-cols-2">
        {fuel === 'DIESEL' ? (
          <>
            <CheckBox
              checked={manualFileOptions.dpf}
              onChange={() =>
                setManualFileOptions({
                  dpf: !manualFileOptions.dpf,
                })
              }
              label="DPF off"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="P codes"
                  id="manualDpfPCodes"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      dpfPCodes: event.target.value,
                    })
                  }
                  value={manualFileOptions.dpfPCodes}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.egr}
              onChange={() =>
                setManualFileOptions({
                  egr: !manualFileOptions.egr,
                })
              }
              label="EGR off"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="P codes"
                  id="manualEgrPCodes"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      egrPCodes: event.target.value,
                    })
                  }
                  value={manualFileOptions.egrPCodes}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.swirlFlap}
              onChange={() =>
                setManualFileOptions({
                  swirlFlap: !manualFileOptions.swirlFlap,
                })
              }
              label="Swirl flap off"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="P codes"
                  id="manualSwirlFlapPCodes"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      swirlFlapPCodes: event.target.value,
                    })
                  }
                  value={manualFileOptions.swirlFlapPCodes}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.hardcut}
              onChange={() =>
                setManualFileOptions({
                  hardcut: !manualFileOptions.hardcut,
                })
              }
              label="Hardcut limiter"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="RPM limiter when driving"
                  id="manualHardcutRpm"
                  error={hardcutRpmError}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      hardcutRpm: event.target.value,
                    })
                  }
                  value={manualFileOptions.hardcutRpm}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.adblue}
              onChange={() =>
                setManualFileOptions({
                  adblue: !manualFileOptions.adblue,
                })
              }
              label="Adblue delete"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="P codes"
                  id="manualAdbluePCodes"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      adbluePCodes: event.target.value,
                    })
                  }
                  value={manualFileOptions.adbluePCodes}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.egt}
              onChange={() =>
                setManualFileOptions({
                  egt: !manualFileOptions.egt,
                })
              }
              label="EGT off"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="P codes"
                  id="manualEgtPCodes"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      egtPCodes: event.target.value,
                    })
                  }
                  value={manualFileOptions.egtPCodes}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.vmax}
              onChange={() =>
                setManualFileOptions({
                  vmax: !manualFileOptions.vmax,
                })
              }
              label="VMAX off"
            />
            <CheckBox
              checked={manualFileOptions.staticRevLimiterRaised}
              onChange={() =>
                setManualFileOptions({
                  staticRevLimiterRaised:
                    !manualFileOptions.staticRevLimiterRaised,
                })
              }
              label="Static rev limiter raised"
            />
            <CheckBox
              checked={manualFileOptions.deepIdle}
              onChange={() =>
                setManualFileOptions({
                  deepIdle: !manualFileOptions.deepIdle,
                })
              }
              label="Deep idle"
            />
            <CheckBox
              checked={manualFileOptions.smokeMap}
              onChange={() =>
                setManualFileOptions({
                  smokeMap: !manualFileOptions.smokeMap,
                })
              }
              label="Smoke map"
            />
            <CheckBox
              checked={manualFileOptions.immoOff}
              onChange={() =>
                setManualFileOptions({
                  immoOff: !manualFileOptions.immoOff,
                })
              }
              label="Immobiliser removal (POA)"
            />
          </>
        ) : (
          <>
            <CheckBox
              checked={manualFileOptions.o2}
              onChange={() =>
                setManualFileOptions({
                  o2: !manualFileOptions.o2,
                })
              }
              label="O2 off"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="P codes"
                  id="manualO2PCodes"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      o2PCodes: event.target.value,
                    })
                  }
                  value={manualFileOptions.o2PCodes}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.swirlFlap}
              onChange={() =>
                setManualFileOptions({
                  swirlFlap: !manualFileOptions.swirlFlap,
                })
              }
              label="Swirl flap off"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="P codes"
                  id="manualPetrolSwirlFlapPCodes"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      swirlFlapPCodes: event.target.value,
                    })
                  }
                  value={manualFileOptions.swirlFlapPCodes}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.ppf}
              onChange={() =>
                setManualFileOptions({
                  ppf: !manualFileOptions.ppf,
                })
              }
              label="PPF/OPF/GPF off"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="P codes"
                  id="manualPpfPCodes"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      ppfPCodes: event.target.value,
                    })
                  }
                  value={manualFileOptions.ppfPCodes}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.popsAndBangs}
              onChange={() =>
                setManualFileOptions({
                  popsAndBangs: !manualFileOptions.popsAndBangs,
                })
              }
              label="Pops and bangs"
            />
            <CheckBox
              checked={manualFileOptions.vmax}
              onChange={() =>
                setManualFileOptions({
                  vmax: !manualFileOptions.vmax,
                })
              }
              label="VMAX off"
            />
            <CheckBox
              checked={manualFileOptions.revLimiter}
              onChange={() =>
                setManualFileOptions({
                  revLimiter: !manualFileOptions.revLimiter,
                })
              }
              label="Rev limiter raised"
            />
            <CheckBox
              checked={manualFileOptions.staticRevLimiterRaised}
              onChange={() =>
                setManualFileOptions({
                  staticRevLimiterRaised:
                    !manualFileOptions.staticRevLimiterRaised,
                })
              }
              label="Static rev limiter raised"
            />
            <CheckBox
              checked={manualFileOptions.hardcut}
              onChange={() =>
                setManualFileOptions({
                  hardcut: !manualFileOptions.hardcut,
                })
              }
              label="Hardcut limiter"
              renderContent={() => (
                <InputGroup
                  noFormik
                  label="RPM limiter when driving"
                  id="manualPetrolHardcutRpm"
                  error={hardcutRpmError}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setManualFileOptions({
                      hardcutRpm: event.target.value,
                    })
                  }
                  value={manualFileOptions.hardcutRpm}
                />
              )}
            />
            <CheckBox
              checked={manualFileOptions.immoOff}
              onChange={() =>
                setManualFileOptions({
                  immoOff: !manualFileOptions.immoOff,
                })
              }
              label="Immobiliser removal (POA)"
            />
          </>
        )}
      </div>
    </div>
  )
}

export default BuildManualFileOptions
